import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../../styles/MemoryGameBasic.css';

interface Card {
  id: number;
  animal: string;
  isFlipped: boolean;
  isMatched: boolean;
}

const ANIMALS = ['🐶', '🐱', '🐰', '🐻', '🦁', '🐯'];

const MemoryGameBasic: React.FC = () => {
  const [cards, setCards] = useState<Card[]>([]);
  const [flippedCards, setFlippedCards] = useState<number[]>([]);
  const [moves, setMoves] = useState(0);
  const [matches, setMatches] = useState(0);
  const [gameWon, setGameWon] = useState(false);

  const initializeGame = () => {
    const gameCards = [...ANIMALS, ...ANIMALS].map((animal, index) => ({
      id: index,
      animal,
      isFlipped: false,
      isMatched: false,
    })).sort(() => Math.random() - 0.5);
    
    setCards(gameCards);
    setFlippedCards([]);
    setMoves(0);
    setMatches(0);
    setGameWon(false);
  };

  useEffect(() => {
    initializeGame();
  }, []);

  const handleCardClick = (cardId: number) => {
    if (flippedCards.length === 2 || cards[cardId].isFlipped || cards[cardId].isMatched) {
      return;
    }

    const newFlippedCards = [...flippedCards, cardId];
    setFlippedCards(newFlippedCards);
    
    setCards(prev => prev.map(card => 
      card.id === cardId ? { ...card, isFlipped: true } : card
    ));

    if (newFlippedCards.length === 2) {
      setMoves(prev => prev + 1);
      
      const [firstId, secondId] = newFlippedCards;
      const firstCard = cards[firstId];
      const secondCard = cards[secondId];
      
      if (firstCard.animal === secondCard.animal) {
        setTimeout(() => {
          setCards(prev => prev.map(card =>
            card.id === firstId || card.id === secondId
              ? { ...card, isMatched: true }
              : card
          ));
          setFlippedCards([]);
          setMatches(prev => {
            const newMatches = prev + 1;
            if (newMatches === ANIMALS.length) {
              setGameWon(true);
            }
            return newMatches;
          });
        }, 500);
      } else {
        setTimeout(() => {
          setCards(prev => prev.map(card =>
            card.id === firstId || card.id === secondId
              ? { ...card, isFlipped: false }
              : card
          ));
          setFlippedCards([]);
        }, 1000);
      }
    }
  };

  return (
    <div className="memory-game-basic">
      <div className="header-basic">
        <Link to="/" className="back-button-basic">← Back to Home</Link>
        <h1>Memory Game</h1>
        <div className="version-badge-basic">Synoppy 1.0</div>
      </div>

      <div className="stats-basic">
        <div>Moves: {moves}</div>
        <div>Matches: {matches}/{ANIMALS.length}</div>
        <button onClick={initializeGame} className="new-game-basic">New Game</button>
      </div>

      {gameWon && (
        <div className="win-message-basic">
          Congratulations! You won in {moves} moves!
        </div>
      )}

      <div className="cards-grid-basic">
        {cards.map((card) => (
          <div
            key={card.id}
            className={`card-basic ${card.isFlipped || card.isMatched ? 'flipped' : ''}`}
            onClick={() => handleCardClick(card.id)}
          >
            {card.isFlipped || card.isMatched ? card.animal : '?'}
          </div>
        ))}
      </div>

      <div className="version-comparison-basic">
        <Link to="?version=1.5" className="upgrade-link">
          Try Synoppy 1.5 Version →
        </Link>
      </div>
    </div>
  );
};

export default MemoryGameBasic;